/**
 * PWA Bundle - Main Entry Point
 *
 * Coordinates all PWA functionality for the Democratic Services theme.
 * This is the main bundle that should be enqueued on the frontend.
 *
 * @package DemocraticServices
 * @since 3.1.0
 * @author Democratic Services Team
 *
 * Features:
 * - Service Worker registration and lifecycle management
 * - Offline action queue with IndexedDB storage
 * - Background sync for reliable form submissions
 * - Auto-save drafts for forms
 * - User feedback for offline/online status
 * - Form interception for seamless offline support
 *
 * Design Decisions:
 * - Modular architecture allows features to be used independently
 * - Progressive enhancement - works without JavaScript fallback
 * - No third-party dependencies as per requirements
 * - All modules are initialized on DOMContentLoaded
 */

// Import PWA modules
import { initDB, getPendingCount } from './offline-queue.js';
import {
	initBackgroundSync,
	syncAllPending,
	getOnlineStatus,
} from './background-sync.js';
import { initDraftManager } from './draft-manager.js';
import {
	initOfflineFeedback,
	updatePendingIndicator,
} from './offline-feedback.js';
import { initFormInterceptor } from './form-interceptor.js';

// =============================================================================
// CONFIGURATION
// =============================================================================

const PWA_CONFIG = {
	// Service worker path is supplied by wp_localize_script.
	serviceWorkerPath: null,

	// Enable/disable features
	features: {
		serviceWorker: true,
		backgroundSync: true,
		draftManager: true,
		offlineFeedback: true,
		formInterceptor: true,
	},

	// Debug mode
	debug: false,
};

// =============================================================================
// SERVICE WORKER REGISTRATION
// =============================================================================

/**
 * Register the service worker
 *
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
async function registerServiceWorker() {
	if (!('serviceWorker' in navigator)) {
		return null;
	}

	try {
		// Determine service worker path
		const swPath = getServiceWorkerPath();

		// Get theme URL for scope
		const themeUrl =
		typeof window.democraticServices !== 'undefined' &&
		window.democraticServices.themeUrl
			? window.democraticServices.themeUrl
			: '';

		const registrationOptions =
			themeUrl !== '' ? { scope: `${themeUrl}/` } : undefined;
		const registration = await navigator.serviceWorker.register(
			swPath,
			registrationOptions,
		);


		// Handle updates
		registration.addEventListener('updatefound', () => {
			const newWorker = registration.installing;


			newWorker?.addEventListener('statechange', () => {
				if (
					newWorker.state === 'installed' &&
          navigator.serviceWorker.controller
				) {
					// New version available
					dispatchPWAEvent('updateAvailable', { registration });
				}
			});
		});

		return registration;
	} catch (error) {
		return null;
	}
}

/**
 * Get the service worker path based on environment
 *
 * @returns {string} Service worker path
 */
function getServiceWorkerPath() {
	// Check for custom path in window config
	if (
		typeof window.democraticServices !== 'undefined' &&
    window.democraticServices.serviceWorkerPath
	) {
		return window.democraticServices.serviceWorkerPath;
	}

	// Safe default when localization is unavailable.
	return '/service-worker.js';
}

/**
 * Update the service worker
 *
 * Forces the waiting service worker to become active.
 */
export async function updateServiceWorker() {
	const registration = await navigator.serviceWorker.ready;

	if (registration.waiting) {
		// Tell the waiting worker to skip waiting
		registration.waiting.postMessage({ type: 'SKIP_WAITING' });

		// Reload page when new worker activates
		navigator.serviceWorker.addEventListener('controllerchange', () => {
			window.location.reload();
		});
	}
}

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Initialize all PWA features
 */
async function initPWA() {
	try {
		// Initialize IndexedDB first
		await initDB();

		// Register Service Worker
		if (PWA_CONFIG.features.serviceWorker) {
			await registerServiceWorker();
		}

		// Initialize Background Sync
		if (PWA_CONFIG.features.backgroundSync) {
			initBackgroundSync();
		}

		// Initialize Draft Manager
		if (PWA_CONFIG.features.draftManager) {
			initDraftManager();
		}

		// Initialize Offline Feedback UI
		if (PWA_CONFIG.features.offlineFeedback) {
			initOfflineFeedback();
		}

		// Initialize Form Interceptor
		if (PWA_CONFIG.features.formInterceptor) {
			initFormInterceptor();
		}

		// Set up message handling from service worker
		setupServiceWorkerMessaging();

		// Check for pending actions
		const pendingCount = await getPendingCount();
		if (pendingCount > 0) {
			if (getOnlineStatus()) {
				syncAllPending();
			}
		}

		// Dispatch ready event
		dispatchPWAEvent('ready', {
			features: PWA_CONFIG.features,
			pendingCount,
		});
	} catch (error) {
		dispatchPWAEvent('error', { error: error.message });
	}
}

/**
 * Set up messaging with the service worker
 */
function setupServiceWorkerMessaging() {
	if (!('serviceWorker' in navigator)) {
		return;
	}

	// Listen for messages from service worker
	navigator.serviceWorker.addEventListener('message', (event) => {
		const { type } = event.data || {};

		switch (type) {
			case 'QUEUE_OFFLINE_ACTION':
				// Service worker queued an action
				updatePendingIndicator();
				break;

			case 'SYNC_COMPLETE':
				break;

			default:
				break;
		}
	});
}

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Democratic Services PWA public API
 *
 * Exposes PWA functionality to other scripts and allows external configuration.
 */
const DemocraticPWA = {
	// Configuration
	config: PWA_CONFIG,

	// Service Worker
	updateServiceWorker,

	// Sync operations
	syncAllPending,
	getOnlineStatus,
	getPendingCount,

	// Manual initialization (if not auto-init)
	init: initPWA,

	// Event helpers
	on: (eventName, handler) => {
		window.addEventListener(`pwa:${eventName}`, handler);
	},

	off: (eventName, handler) => {
		window.removeEventListener(`pwa:${eventName}`, handler);
	},
};

// Export to window for global access
if (typeof window !== 'undefined') {
	// Preserve any lightweight inline helpers (e.g. getDraft/saveDraft/queueOfflineAction)
	// that may have been injected before this bundle loads. Merge them so templates
	// using the helpers still work after the bundle overwrites the global.
	try {
		const _existing = window.DemocraticPWA || {};
		// Merge existing helper functions with the bundle API, giving priority
		// to bundle methods when names conflict.
		window.DemocraticPWA = Object.assign({}, _existing, DemocraticPWA);
	} catch (e) {
		// Fallback to direct assignment if merge fails for any reason
		window.DemocraticPWA = DemocraticPWA;
	}
}

// =============================================================================
// EVENT DISPATCHING
// =============================================================================

/**
 * Dispatch a custom PWA event
 *
 * @param {string} eventName - Event name
 * @param {Object} detail - Event detail
 */
function dispatchPWAEvent(eventName, detail = {}) {
	if (typeof window !== 'undefined') {
		const event = new CustomEvent(`pwa:${eventName}`, { detail });
		window.dispatchEvent(event);
	}
}

// =============================================================================
// AUTO-INITIALIZATION
// =============================================================================

// Initialize when DOM is ready
if (typeof window !== 'undefined') {
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initPWA);
	} else {
		// DOM already loaded
		initPWA();
	}
}

export default DemocraticPWA;
